import React, { memo, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes } from 'styled-components';
import isNil from 'lodash/isNil';

import { NavigationContext, TOGGLE_MOBILE_DRAWER } from '../context/NavigationContext';
import getStyles from '../util/getStyles';

const MENU_WIDTH = 300;
const OVERLAY_DEFAULT_COLOR = 'rgba(0, 0, 0, 0.75)';

const defaultStyles = {
    color: 'white',
    display: 'flex',
    position: 'absolute',
    width: `${MENU_WIDTH}px`,
    top: '0',
    flexDirection: 'column',
    zIndex: '2',
};

const typeStyles = {
    left: {
        height: '100vh',
        right: 'auto',
        top: '0',
    },
    bottom: {
        top: '-100%',
    },
};

const openKeyframes = navAlignment => keyframes`
    from { transform: translateX(${navAlignment === 'right' ? '150%' : '0'}); }
    to { transform: translateX(${navAlignment === 'right' ? '0' : '100%'}); }
;`;

const closedKeyframes = navAlignment => keyframes`
    from { transform: translateX(${navAlignment === 'right' ? '0' : '100%'}) }
    to { transform: translateX(${navAlignment === 'right' ? '150%' : '0%'}); }
;`;

const Wrapper = styled.div`
    position: fixed;
    top: ${({ navAlignment }) => (navAlignment === 'bottom' || navAlignment === 'top' ? '100%' : '0')};;
    left: ${({ navAlignment }) => {
        switch (navAlignment) {
            case 'right':
                return '100%';
            case 'left':
                return '0%';
            case 'top':
                return '50%';
            case 'bottom':
                return '50%';
            default:
                return '50%';
        }
    }};
    width: 100%;
    height: calc(100vh - 100%);

    transform: ${({ navAlignment }) => {
        switch (navAlignment) {
            case 'right':
                return 'translateX(0%)';
            case 'left':
                return 'translateX(0%)';
            case 'top':
                return 'translateX(-50%)';
            case 'bottom':
                return 'translateX(-50%)';
            default:
                return 'translateX(0)';
        }
    }};
    z-index: 0;
    pointer-events: none !important;
`;

const Overlay = styled.div`
    top: 100%;
    left: 0;
    right: 0;
    position: fixed;
    width: 100vw;
    height: 100vh;
    transform: translateY(-100%);
    z-index: 1;
    transition: opacity 0.5s;

    ${({ styles: { color = OVERLAY_DEFAULT_COLOR, blur = 0 }, menuOpen, navAlignment }) => `
        background: ${color};
        backdrop-filter: blur(${blur});
        opacity: ${(menuOpen && '1') || '0'};
        pointer-events: ${(menuOpen && 'auto') || 'none'};
        top:${(navAlignment === 'bottom' && '0')};
        left:${navAlignment === 'right' && '-100%'};
    `}
`;

const Menu = styled.div`
    height: 100%;

    &:before {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: 100%;
        display: block;
        width: 100%;
        background: inherit;
        height: 50%;
    }

    ${({ styles = {} }) => styles}
    left: ${({ alignment }) => (alignment === 'left' ? `-${MENU_WIDTH + 1}px` : `calc(100% - ${MENU_WIDTH - 1}px)`)};
    transform: translateX(${({ navAlignment }) => (navAlignment === 'right' ? '150%' : '0%')});

    ${({ menuOpen, alignment, navAlignment }) => !isNil(menuOpen) && css`
        animation: ${(alignment === 'left' ? menuOpen : !menuOpen) ? openKeyframes(navAlignment)
        : closedKeyframes(navAlignment)} 0.5s forwards;
    `}

    pointer-events: all !important;
`;

const DrawerContainer = ({ styles: { alignment = 'left', ...elemStyles } = {}, children, ...rest }) => {
    const {
        state: { menuOpen },
        dispatch,
        onHelmet,
        navAlignment,
    } = useContext(NavigationContext);
    const { rootStyles = {}, childrenStyles = {} } = getStyles(elemStyles);
    const { overlay = {} } = childrenStyles;

    const handleOverlayClick = () => {
        if (menuOpen) {
            onHelmet({ previous: true, next: false });
            dispatch({ type: TOGGLE_MOBILE_DRAWER });
        }
    };

    return useMemo(
        () => (
            <Wrapper navAlignment={navAlignment}>
                <Menu
                    {...rest}
                    menuOpen={menuOpen}
                    alignment={alignment}
                    navAlignment={navAlignment}
                    styles={{ ...defaultStyles, ...rootStyles, ...typeStyles[navAlignment] }}
                >
                    {children}
                </Menu>
                <Overlay
                    navAlignment={navAlignment}
                    menuOpen={menuOpen}
                    styles={overlay}
                    onClick={handleOverlayClick}
                />
            </Wrapper>
        ),
        [menuOpen, navAlignment],
    );
};
DrawerContainer.propTypes = {
    children: PropTypes.node,
    styles: PropTypes.object,
};

export default memo(DrawerContainer);
